import * as React from 'react'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

/** Tipo mínimo requerido para el multi-selector de clientes */
interface ClienteBasico {
  id: string | null
  nombre: string | null
  cuit?: number | string | null
}

interface ClientesMultiSelectorProps {
  clientes: ClienteBasico[]
  /** IDs de clientes seleccionados */
  value: string[]
  onValueChange: (ids: string[]) => void
  disabled?: boolean
  className?: string
}

/**
 * Multi-selector de clientes puntuales (lista plana, sin agrupación).
 * Búsqueda por nombre/CUIT (patrón cliente-combobox) + popover con checkboxes
 * y badges de seleccionados (patrón impuestos-selector). Devuelve IDs (string[]).
 */
export function ClientesMultiSelector({
  clientes,
  value,
  onValueChange,
  disabled = false,
  className,
}: ClientesMultiSelectorProps) {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')

  const clientesValidos = React.useMemo(
    () => clientes.filter((c) => c.id && c.nombre),
    [clientes]
  )
  const selectedSet = React.useMemo(() => new Set(value), [value])
  const selectedClientes = clientesValidos.filter((c) => selectedSet.has(c.id!))

  const filtered = React.useMemo(() => {
    if (!searchQuery) return clientesValidos
    const query = searchQuery.toLowerCase()
    return clientesValidos.filter((c) => {
      const nombre = c.nombre?.toLowerCase() ?? ''
      const cuit = c.cuit?.toString() ?? ''
      return nombre.includes(query) || cuit.includes(query)
    })
  }, [clientesValidos, searchQuery])

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      onValueChange(value.filter((v) => v !== id))
    } else {
      onValueChange([...value, id])
    }
  }

  return (
    <div className={cn('space-y-2', className)}>
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between"
          >
            <span className={selectedClientes.length ? '' : 'text-muted-foreground'}>
              {selectedClientes.length
                ? `${selectedClientes.length} cliente${selectedClientes.length > 1 ? 's' : ''} seleccionado${selectedClientes.length > 1 ? 's' : ''}`
                : 'Seleccionar clientes...'}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[500px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Buscar por nombre, CUIT..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList className="max-h-[250px] overflow-y-auto overscroll-contain">
              <CommandEmpty>
                {searchQuery ? 'No se encontraron clientes.' : 'No hay clientes disponibles.'}
              </CommandEmpty>
              <CommandGroup>
                {filtered.slice(0, 100).map((cliente) => (
                  <CommandItem
                    key={cliente.id}
                    value={cliente.id!}
                    onSelect={() => toggle(cliente.id!)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selectedSet.has(cliente.id!) ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <div className="flex flex-col">
                      <span className="font-medium">{cliente.nombre}</span>
                      {cliente.cuit && (
                        <span className="text-xs text-muted-foreground">
                          CUIT: {cliente.cuit}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
                {filtered.length > 100 && (
                  <div className="p-2 text-center text-xs text-muted-foreground border-t">
                    Mostrando 100 de {filtered.length} resultados. Refina tu búsqueda.
                  </div>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedClientes.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedClientes.map((cliente) => (
            <Badge key={cliente.id} variant="secondary" className="gap-1">
              {cliente.nombre}
              <button
                type="button"
                onClick={() => toggle(cliente.id!)}
                className="ml-0.5 rounded-full hover:bg-muted-foreground/20"
                aria-label={`Quitar ${cliente.nombre}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}
